/**
 * tenant-primary.ts — runtime tenant-brand primary with OKLCH-derived shade ramps.
 *
 * Per CONTEXT D-12 the interpolation fractions are LOCKED (match bundle tokens.css
 * within acceptable perceptual delta for the default `#C85C2E` terracotta):
 *   -50  = interpolate([P, 'white'], 'oklch')(0.85)
 *   -100 = interpolate([P, 'white'], 'oklch')(0.65)
 *   -700 = interpolate([P, 'black'], 'oklch')(0.22)
 *   -800 = interpolate([P, 'black'], 'oklch')(0.35)
 *   --tenant-on-primary = white when it clears 3:1 on P, else the rail ink (onPrimaryFor)
 *
 * LIGHT + DARK, never the semantic names. This module writes RAW `--tenant-*`
 * custom properties INLINE on documentElement, in a light set and a `-dark`
 * set; `colors/semantic.css` and `dark.css` alias the app-facing
 * `--color-primary*` names onto the right set, with the static tokens as the
 * no-tenant fallback. Writing `--color-*` directly (as this module used to)
 * cannot work: an inline custom property on the root element outranks every
 * stylesheet rule, so `dark.css` could never override it and the light tint
 * leaked into dark mode — a selected nav row painted a near-white `-50` fill
 * under light-in-dark-mode `ink-2` text. The Majlis layer below has always
 * done it this way; this is the same pattern applied to the primary ramp.
 *
 * The dark set mirrors the ramp around the DARK theme's surface: `-50`/`-100`
 * tint toward `--surface` (dark) at the same fractions the light set tints
 * toward white, and `-700`/`-800` — which are only ever FOREGROUNDS — tint
 * toward white so they stay the "stronger" end of the ramp on a dark tint. A
 * brand too dark to read on the dark surface is lifted first (`liftForDark`),
 * otherwise a navy brand's dark tint is indistinguishable from the page.
 *
 * `setTenantPrimary(hex)` mutates `document.documentElement.style`; `validateTenantPrimary(hex)`
 * returns the same derived shape WITHOUT mutating DOM (used by Brand Studio preview).
 *
 * Ink fallback `#1E1A16` is the bundle's raw `--ink` token — inlined here as a
 * hex literal because the runtime layer can't read CSS variables before paint.
 * ESLint `no-hex-colors` is disabled for `packages/design-system/src/runtime/**`
 * (Wave 1 Plan 01 Task 3 override).
 */
import {
  interpolateToWhite,
  interpolateToBlack,
  interpolateToward,
  getLightness,
  parseHex,
} from "./color";
import { contrastRatio, wcagVerdict, WCAG_THRESHOLDS, type WcagVerdict } from "./contrast";
import { deriveMajlisCssVars } from "./majlis";

const INK = "#0E1621";
const WHITE = "#FFFFFF";

/** `--surface` under `[data-theme="dark"]` (tokens/dark.css) — the dark set's tint target. */
const DARK_SURFACE = "#121C28";

/**
 * Default Link HR teal primary — the neutral fallback used when no tenant
 * branding is available (login screens pre-auth, platform audience, branding
 * fetch failure). Exported for consumers that need to reference the same
 * canonical hex without triggering the `no-hex-colors` ESLint ban at their
 * call site.
 */
export const DEFAULT_PRIMARY_HEX = "#5AB6CB";

/**
 * Ready-made brand colours offered next to a custom hex (setup's Brand
 * screen): the default teal first, then colours whose text reads at AA
 * (≥4.5:1) on white.
 */
export const BRAND_SWATCHES: readonly string[] = [
  DEFAULT_PRIMARY_HEX,
  "#1E3A5F",
  "#0E6A5F",
  "#3F6B34",
  "#6A3A9E",
  "#B4532A",
];

/**
 * Text color to place on a primary fill. White while it clears the WCAG 3:1
 * UI floor; otherwise the rail ink. The default teal (#5AB6CB) puts white at
 * 2.34:1 and ink at 7.8:1, so it resolves to ink — while a terracotta-style
 * tenant primary (white 4.2:1) keeps white text exactly as before, never the
 * "razor-close ink on a warm fill" flip that an always-max rule produced.
 */
export function onPrimaryFor(hex: string): string {
  return contrastRatio(WHITE, hex) >= WCAG_THRESHOLDS.BLOCK_MAX ? WHITE : INK;
}

export interface SetTenantPrimaryResult {
  /** Map of the 7 CSS custom properties written (or derivable) from the input hex. */
  derivedShades: Record<string, string>;
  /** WCAG contrast ratio between the input hex and its resolved on-primary text color. */
  ratio: number;
  /** Four-level verdict — BLOCK / WARN / AA / AAA (CONTEXT D-12). */
  verdict: WcagVerdict;
}

export interface ValidateTenantPrimaryResult extends SetTenantPrimaryResult {
  /**
   * Human-readable hint surfaced when:
   *   - input is invalid (parse failure)
   *   - input OKLCH lightness < 0.25 (dark primaries crush -700/-800 to identical black)
   *   - input OKLCH lightness > 0.9 (too light → insufficient AA contrast on white)
   *   - verdict is WARN (buttons still correct via --color-text-on-primary; body text fails AA)
   */
  hint?: string;
}

const SHADE_FRACTIONS = {
  light50: 0.85,
  light100: 0.65,
  dark700: 0.22,
  dark800: 0.35,
} as const;

/**
 * The brand as it is usable in DARK mode: the input hex if it already clears
 * WCAG AA body contrast against the dark surface, otherwise the same hue
 * walked toward white until it does. A navy or maroon brand is invisible on
 * the dark page otherwise — its fill fails the 3:1 UI floor, its `-50` tint is
 * indistinguishable from the surface, and `text-primary` disappears. Light
 * mode is untouched: this value is only ever read through the `-dark` set.
 */
export function liftForDark(hex: string): string {
  for (let step = 0; step <= 18; step += 1) {
    const t = step * 0.05;
    const candidate = step === 0 ? hex : interpolateToWhite(hex, t);
    if (contrastRatio(candidate, DARK_SURFACE) >= WCAG_THRESHOLDS.WARN_MAX) return candidate;
  }
  return interpolateToWhite(hex, 0.9);
}

function deriveShades(hex: string): Record<string, string> {
  const dark = liftForDark(hex);
  return {
    "--tenant-primary": hex,
    "--tenant-primary-50": interpolateToWhite(hex, SHADE_FRACTIONS.light50),
    "--tenant-primary-100": interpolateToWhite(hex, SHADE_FRACTIONS.light100),
    "--tenant-primary-700": interpolateToBlack(hex, SHADE_FRACTIONS.dark700),
    "--tenant-primary-800": interpolateToBlack(hex, SHADE_FRACTIONS.dark800),
    "--tenant-on-primary": onPrimaryFor(hex),

    // Dark set — the same ramp mirrored around the dark surface. `-50`/`-100`
    // tint toward `--surface` (dark) instead of white; `-700`/`-800` tint
    // toward white because on a dark tint the "stronger" ramp end is lighter.
    "--tenant-primary-dark": dark,
    "--tenant-primary-50-dark": interpolateToward(dark, DARK_SURFACE, SHADE_FRACTIONS.light50),
    "--tenant-primary-100-dark": interpolateToward(dark, DARK_SURFACE, SHADE_FRACTIONS.light100),
    "--tenant-primary-700-dark": interpolateToWhite(dark, SHADE_FRACTIONS.dark700),
    "--tenant-primary-800-dark": interpolateToWhite(dark, SHADE_FRACTIONS.dark800),
    "--tenant-on-primary-dark": onPrimaryFor(dark),

    // Majlis / Iwan derivation layer (F-1). Additive: derives the money-panel +
    // domain-chip pairs + brass/Iwan chrome from the brand hue and writes both a
    // light set (`--majlis-*`) and a dark set (`--majlis-*-dark`). dark.css
    // aliases the semantic names onto the `-dark` variants under
    // `[data-theme="dark"]` — necessary because these INLINE writes on
    // documentElement outrank the dark stylesheet, so a single-set write would
    // leak the light money-green into dark mode.
    ...deriveMajlisCssVars(hex),
  };
}

/**
 * Applies tenant brand primary to `document.documentElement` as raw
 * `--tenant-*` custom properties — a light set and a `-dark` set. The
 * stylesheets alias `--color-primary*` / `--color-text-on-primary` onto
 * whichever set the active theme calls for; nothing here writes a `--color-*`
 * name, because an inline write of one would outrank `dark.css`.
 *
 * Returns the derived shade map + WCAG verdict vs white. If the input is invalid,
 * returns `{ ratio: 1, verdict: 'BLOCK' }` and does NOT mutate the DOM — caller
 * can surface an error without rolling back any partial mutation.
 */
export function setTenantPrimary(hex: string): SetTenantPrimaryResult {
  const parsed = parseHex(hex);
  if (!parsed) {
    return { derivedShades: {}, ratio: 1, verdict: "BLOCK" };
  }

  const derivedShades = deriveShades(hex);
  const ratio = contrastRatio(hex, onPrimaryFor(hex));
  const verdict = verdictFor(hex, ratio);

  const root = document.documentElement;
  // Clear any inline tenant vars from a PRIOR set that this call does NOT
  // re-write, then write the current set. Without this, a previous colorful
  // brand could leave stale Majlis vars behind if a later brand ever derived a
  // narrower key set (F1). Achromatic brands now derive the full set, so in
  // practice every valid hex writes identical keys — this clearing pass is the
  // belt-and-suspenders guarantee that setTenantPrimary NEVER leaves a stale
  // inline var from a prior brand.
  clearStalePrimaryVars(root, derivedShades);
  for (const [prop, value] of Object.entries(derivedShades)) {
    root.style.setProperty(prop, value);
  }

  return { derivedShades, ratio, verdict };
}

/**
 * Verdict for a primary: the WCAG band of its fill-vs-text ratio, except that
 * a near-white primary is BLOCKed outright. With ink text such a fill would
 * pass the text check and still be an invisible button on a white card.
 */
function verdictFor(hex: string, ratio: number): WcagVerdict {
  const lightness = getLightness(hex) ?? 0.5;
  if (lightness > 0.9) return "BLOCK";
  return wcagVerdict(ratio);
}

/**
 * Removes any previously-written tenant primary / Majlis inline custom property
 * that the incoming `next` map does not re-write. Derives the "everything this
 * layer can write" universe from a canonical probe (the default brand yields the
 * complete key set), so the removal list stays in sync with `deriveShades`
 * automatically — no hand-maintained list to drift.
 */
function clearStalePrimaryVars(root: HTMLElement, next: Record<string, string>): void {
  for (const prop of ALL_PRIMARY_VAR_KEYS) {
    if (!(prop in next)) root.style.removeProperty(prop);
  }
}

/**
 * The complete set of inline custom-property names `deriveShades` can emit,
 * probed once from the default brand. Every valid hex produces this exact key
 * set (achromatic included, post-F1), so this doubles as the stale-var removal
 * universe.
 */
const ALL_PRIMARY_VAR_KEYS: readonly string[] = Object.keys(deriveShades(DEFAULT_PRIMARY_HEX));

/**
 * Pure validation — returns the same shape as `setTenantPrimary` WITHOUT
 * mutating the DOM. Adds a human-readable `hint` for edge cases so the Brand
 * Studio UI (Phase 3+) can surface actionable guidance.
 */
export function validateTenantPrimary(hex: string): ValidateTenantPrimaryResult {
  const parsed = parseHex(hex);
  if (!parsed) {
    return {
      derivedShades: {},
      ratio: 1,
      verdict: "BLOCK",
      hint: "Invalid color — expected #RRGGBB format.",
    };
  }

  const derivedShades = deriveShades(hex);
  const ratio = contrastRatio(hex, onPrimaryFor(hex));
  const verdict = verdictFor(hex, ratio);

  let hint: string | undefined;
  const lightness = getLightness(hex) ?? 0.5;
  if (lightness < 0.25) {
    hint =
      "Primary is too dark — choose a lighter color (OKLCH L ≥ 0.25) or -700/-800 shades will crush to identical black.";
  } else if (lightness > 0.9) {
    hint =
      "Primary is too light — choose a darker color for sufficient AA contrast on white surfaces.";
  } else if (verdict === "WARN") {
    hint =
      "Primary sits below WCAG 2.1 AA body-text contrast against its button text. Buttons using --color-text-on-primary stay legible (large text floor); do NOT place body text on bg-primary surfaces.";
  }

  return {
    derivedShades,
    ratio,
    verdict,
    ...(hint ? { hint } : {}),
  };
}
